"use client";

import { useEffect, useState, type ReactNode } from "react";
import { Check, X, AlertTriangle, Loader2, ExternalLink } from "lucide-react";
import { Button } from "@stockline/ui/components/button";
import { Badge } from "@stockline/ui/components/badge";
import { useTranslation } from "react-i18next";
import { useUserOptional } from "@/components/auth-provider";
import { CompanyPermissions } from "@/app/api/types/superadmin";
import {
  STATUS_STYLES,
  translateTitle,
  type ProposalCardInnerProps,
} from "./proposal-card-shared";

interface ProposalCardShellProps extends ProposalCardInnerProps {
  readonly children: ReactNode;
  /**
   * When true, the approve button is disabled even though `canAct` is true.
   * Used by entity cards (e.g. group prices) that require all rows resolved
   * before the batch can be approved. Omit to keep default behavior.
   */
  readonly approveDisabled?: boolean;
  /**
   * Optional control rendered in the header row, before the status badge.
   * Used by entity cards to surface a contextual action (e.g. a link to the
   * full proposals page). Omit when no header action is needed.
   */
  readonly headerAction?: ReactNode;
}

export function ProposalCardShell({
  batch,
  showTimestamp,
  acting,
  canAct,
  onApprove,
  onReject,
  approveDisabled,
  headerAction,
  children,
}: ProposalCardShellProps) {
  const { t } = useTranslation();
  const style = STATUS_STYLES[batch.status] ?? STATUS_STYLES.pending;
  const StatusIcon = style.icon;
  // Defer locale-formatted timestamp to client-only render to avoid SSR
  // hydration mismatches between server timezone and client timezone.
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  // "View trace" link surfaces only for OWNER / SUPER_ADMIN so end-customer
  // users (EMPLOYEE) never see the internal Langfuse URL. Renders only when
  // the AI service captured a trace coords on this batch.
  const user = useUserOptional();
  const canSeeTrace =
    user?.company_permissions === CompanyPermissions.OWNER ||
    user?.company_permissions === CompanyPermissions.SUPER_ADMIN;
  // Defense in depth — the V2 API already z.string().url() validates this,
  // but the source ultimately comes from a third-party SDK so we reject
  // anything that isn't an https:// URL before rendering it as a href.
  const safeTraceUrl =
    typeof batch.langfuseTraceUrl === "string" &&
    batch.langfuseTraceUrl.startsWith("https://")
      ? batch.langfuseTraceUrl
      : null;
  const showTraceLink = canSeeTrace && safeTraceUrl !== null;

  return (
    <div
      className={`rounded-lg border overflow-hidden ${style.className}`}
      data-testid={`proposal-card-${batch.id}`}
    >
      {/* Header */}
      <div className="flex items-center gap-2 px-3 py-2">
        <StatusIcon className="h-4 w-4 shrink-0" />
        <span className="text-sm font-medium truncate flex-1">
          {translateTitle(batch.title, t)}
        </span>
        {showTimestamp && batch.createdAt && mounted && (
          <span className="text-xs text-muted-foreground shrink-0">
            {new Date(batch.createdAt).toLocaleString()}
          </span>
        )}
        {headerAction}
        <Badge
          variant={style.variant}
          className="text-xs shrink-0"
          data-testid="proposal-status"
        >
          {t(`aiProposals.${style.key}`)}
        </Badge>
      </div>

      {/* Description */}
      {batch.description && (
        <div className="px-3 pb-1 text-xs text-muted-foreground">
          {batch.description}
        </div>
      )}

      {/* Content (entity-specific) */}
      {children}

      {/* Failure reason */}
      {batch.failureReason && (
        <div className="px-3 pb-2">
          <div className="flex items-center gap-1.5 text-xs text-destructive">
            <AlertTriangle className="h-3 w-3 shrink-0" />
            <span>{batch.failureReason}</span>
          </div>
        </div>
      )}

      {/* Internal: View Langfuse trace */}
      {showTraceLink && (
        <div className="px-3 pb-2">
          <a
            href={safeTraceUrl ?? undefined}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground underline"
            data-testid="proposal-trace-link"
          >
            <ExternalLink className="h-3 w-3" />
            {t("aiProposals.viewTrace", "View trace")}
          </a>
        </div>
      )}

      {/* Actions */}
      {canAct && (
        <div className="flex items-center justify-end gap-2 px-3 py-2 border-t border-border/50 bg-background/40">
          <Button
            size="sm"
            variant="outline"
            onClick={onReject}
            disabled={acting}
            className="h-7 gap-1.5 text-xs"
            data-testid="proposal-reject-button"
          >
            <X className="h-3 w-3" />
            {t("aiProposals.reject")}
          </Button>
          <Button
            size="sm"
            onClick={onApprove}
            disabled={acting || approveDisabled}
            className="h-7 gap-1.5 text-xs"
            data-testid="proposal-approve-button"
          >
            {acting ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : (
              <Check className="h-3 w-3" />
            )}
            {t("aiProposals.approve")}
          </Button>
        </div>
      )}
    </div>
  );
}
